import ButtonIconText from '@omni/kit/components/ButtonIconText/ButtonIconText';
import { IconName } from '@omni/kit/components/KitIcon';
import KitText from '@omni/kit/components/KitText';
import Popover, { TargetProps } from '@omni/kit/components/Popover';
import { useShellContext } from '@omni/kit/contexts/ShellContext';
import { useTranslation } from '@omni/kit/hooks/useTranslation';
import Colors from '@omni/kit/theming/Colors';
import Spacing from '@omni/kit/theming/Spacing';
import { openLink } from '@omni/kit/utilities/links';
import React, { ReactElement } from 'react';
import { View } from 'react-native';

import { copyToClipboard } from './copyToClipboard';

const DEFAULT_ICON_SIZE = 20;
const SHARE_MODAL_HEIGHT = 161;
const SHARE_MODAL_WIDTH = 300;

interface IProps {
  shortLink?: string;
  isOpen: boolean;
  anchorBottom: boolean;
  onClose?: () => void;
  targetRef: TargetProps;
  onSendAnalytics?: (shareTo: string) => void;
}

interface Button {
  icon: IconName;
  iconBackgroundColor: string;
  iconColor: string;
  iconSize: number;
  onPress: () => void;
  text: string;
}

export default ({
  isOpen = false,
  anchorBottom = false,
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  onClose = () => {},
  shortLink = '',
  targetRef,
  onSendAnalytics,
}: IProps): ReactElement => {
  const { t } = useTranslation(['mediaDetail']);
  const { dispatchEvent } = useShellContext();

  const buttons: Button[] = [
    {
      icon: 'linked',
      iconColor: Colors.N1000,
      iconSize: DEFAULT_ICON_SIZE,
      iconBackgroundColor: Colors.N100,
      text: t('copyLink'),
      onPress: () => {
        copyToClipboard(shortLink);
        onClose();
        dispatchEvent({
          type: 'SET_SNACKBAR_MESSAGE',
          payload: t('linkCopied'),
        });
        onSendAnalytics?.('link-copied');
      },
    },
    {
      icon: 'facebook',
      iconBackgroundColor: Colors.facebook,
      iconColor: Colors.N0,
      iconSize: DEFAULT_ICON_SIZE,
      text: t('facebook'),
      onPress: () => {
        openLink(
          `https://www.facebook.com/dialog/share?app_id=462626960493104&display=popup&href=${shortLink}`
        );
        onClose();
        onSendAnalytics?.('facebook');
      },
    },
    {
      icon: 'x-logo',
      iconColor: Colors.N0,
      iconSize: DEFAULT_ICON_SIZE,
      iconBackgroundColor: Colors.N1000,
      text: t('x'),
      onPress: () => {
        openLink(`https://twitter.com/intent/tweet?url=${shortLink}`);
        onClose();
        onSendAnalytics?.('x');
      },
    },
  ];

  return (
    <Popover
      height={SHARE_MODAL_HEIGHT}
      isOpen={isOpen}
      anchorBottom={anchorBottom}
      onClose={onClose}
      targetRef={targetRef}
      width={SHARE_MODAL_WIDTH}
      position='left-start'
    >
      <View>
        <KitText
          black
          bold
          numberOfLines={1}
          fontSize={22}
          style={{
            paddingHorizontal: Spacing.l,
            paddingTop: Spacing.l,
          }}
        >
          {t('mediaDetail:share')}
        </KitText>
        <View
          style={{
            display: 'flex',
            flexDirection: 'row',
            justifyContent: 'space-evenly',
            marginTop: Spacing.l,
          }}
        >
          {buttons.map((button, key) => (
            <ButtonIconText
              key={key}
              onPress={button.onPress}
              icon={button.icon}
              iconBackgroundColor={button.iconBackgroundColor}
              iconColor={button.iconColor}
              text={button.text}
            />
          ))}
        </View>
      </View>
    </Popover>
  );
};
